// Copyright (c) 2025 Goffy. All Rights Reserved. This file belongs to Goffy.

import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Platform, Image, Modal, useWindowDimensions } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useRouter, useRootNavigationState, useLocalSearchParams } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Video, ResizeMode } from 'expo-av';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import { getSharedBffWebSocket, PlayersErrorEvent, PlayersSnapshotEvent, TourUpdateEvent, TourCreatedEvent } from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { setCurrentTourId } from '@/services/currentTour';
import { setCurrentHole, setCurrentRoundId } from '@/services/currentRound';
import { useUser } from '@/app/user-context';
import { UserBadge } from '@/components/UserBadge';
import { useRoundState } from '@/stores/roundState';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import ChooseGameImage from '@/assets/images/select-games.png';
import BestBallSelectImage from '@/assets/images/games/best-ball-select.png';
import ThreePuttsSelectImage from '@/assets/images/games/3-putts-select.png';
import BunkerSelectImage from '@/assets/images/games/bunker-select.png';
import HighLowSelectImage from '@/assets/images/games/high-low-select.png';
import StablefordSelectImage from '@/assets/images/games/stableford-select.png';
import SkinsSelectImage from '@/assets/images/games/skins-select.png';
import MysterySelectImage from '@/assets/images/games/mystery-select.png';
import PenaltySelectImage from '@/assets/images/games/penalty-select.png';
import WaterSelectImage from '@/assets/images/games/water-select.png';
import WorstBallSelectImage from '@/assets/images/games/worst-ball-select.png';

type Player = {
  id: string;
  name: string;
  createdAt: string;
};

type PlayersResponse = {
  players?: Player[];
  tourId?: string;
  Players?: Player[];
  TourId?: string;
};

type RoundResponse = {
  courseName?: string;
  CourseName?: string;
  tourId?: string;
  TourId?: string;
  gameDefinitionIds?: string[];
  GameDefinitionIds?: string[];
};

type GameDefinition = {
  id: string;
  name: string;
  info?: string;
  description?: string;
  competitionType?: string;
  status?: string;
  image?: string;
  video?: string;
  videoWebWidth?: number;
  videoWebHeight?: number;
  videoMobileWidth?: number;
  videoMobileHeight?: number;
};

const DELIVERY_ASSET_BASE_URL = process.env.EXPO_PUBLIC_FRONTEND_ASSET_BASE_URL ?? 'https://d2z2xup96uar48.cloudfront.net/assets-generated';
const MOBILE_VIDEO_BREAKPOINT = 900;

const GAME_IMAGE_MAP: Record<string, number> = {
  'best-ball': BestBallSelectImage,
  '3-putts': ThreePuttsSelectImage,
  bunker: BunkerSelectImage,
  'high-low': HighLowSelectImage,
  mystery: MysterySelectImage,
  penalty: PenaltySelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  water: WaterSelectImage,
  'worst-ball': WorstBallSelectImage,
};

export default function SelectGamesScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { width: viewportWidth } = useWindowDimensions();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user } = useUser();
  const { debug: debugParam, roundId: roundIdParam } = useLocalSearchParams<{ debug?: string; roundId?: string }>();
  const storedRoundId = useRoundState((state) => state.roundId);
  const [tourId, setTourId] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [courseName, setCourseName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameDefs, setGameDefs] = useState<GameDefinition[]>([]);
  const [gamesError, setGamesError] = useState<string | null>(null);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [players, setPlayers] = useState<Player[]>([]);
  const [starting, setStarting] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);
  const [statusNote, setStatusNote] = useState<string>('Laddar spel och spelare...');
  const gamesLoadedRef = useRef(false);
  const prefilledGamesRef = useRef(false);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundId = roundIdFromParams ?? storedRoundId;
  const pendingRoundRequestIdRef = useRef<string | null>(null);
  const roundAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const infoVideoRef = useRef<Video>(null);
  const useMobileVideoLayout = viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT;
  const infoVideoUri = useMemo(
    () => resolveGameVideoUri(infoGame?.video, apiBaseUrl, viewportWidth),
    [apiBaseUrl, infoGame?.video, viewportWidth]
  );
  const infoVideoLayout = useMemo(
    () => resolveInfoVideoLayout(infoGame, useMobileVideoLayout),
    [infoGame, useMobileVideoLayout]
  );
  const roundIdIsValid = useMemo(() => !!roundId && isGuid(roundId), [roundId]);

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (roundIdFromParams && roundIdFromParams !== storedRoundId) {
      setCurrentRoundId(roundIdFromParams);
    }
  }, [roundIdFromParams, storedRoundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'select-games',
      }),
    [apiBaseUrl, user?.token]
  );

  const closeInfo = () => {
    infoVideoRef.current?.stopAsync().catch(() => {});
    setInfoOpen(false);
    setInfoGame(null);
  };

  useEffect(() => {
    if (!isNavigationReady || !user?.token) {
      setLoading(false);
      return;
    }
    const fetchSnapshot = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`${t('players.loadError')} (${response.status})`);
        }
        const data = (await response.json()) as PlayersResponse;
        const incomingPlayers = data.players ?? data.Players ?? [];
        setPlayers(incomingPlayers);
        const incomingTourId = data.tourId ?? data.TourId;
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        setError(null);
        setStatusNote('');
      } catch {
        setError(t('players.loadError'));
        setStatusNote('Kunde inte hämta spelare.');
      } finally {
        setLoading(false);
      }
    };

    const fetchGames = async () => {
      if (gamesLoadedRef.current && !gamesError) {
        return;
      }
      if (!user?.token) {
        return;
      }
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error();
        }
        const data = (await response.json()) as GameDefinition[];
        const normalized = data.map((g) => ({
          ...g,
          competitionType: g.competitionType ?? (g as { CompetitionType?: string }).CompetitionType ?? undefined,
          status: g.status ?? (g as { Status?: string }).Status ?? undefined,
          image: g.image ?? (g as { Image?: string }).Image ?? undefined,
          video: g.video ?? (g as { Video?: string }).Video ?? undefined,
          videoWebWidth: g.videoWebWidth ?? (g as { VideoWebWidth?: number }).VideoWebWidth ?? undefined,
          videoWebHeight: g.videoWebHeight ?? (g as { VideoWebHeight?: number }).VideoWebHeight ?? undefined,
          videoMobileWidth: g.videoMobileWidth ?? (g as { VideoMobileWidth?: number }).VideoMobileWidth ?? undefined,
          videoMobileHeight: g.videoMobileHeight ?? (g as { VideoMobileHeight?: number }).VideoMobileHeight ?? undefined,
        }));
        const enabledGames = normalized.filter((g) => !g.status || (g.status ?? '').toLowerCase() === 'enabled');
        const resolvedGames = enabledGames.length > 0 ? enabledGames : normalized;
        setGameDefs(resolvedGames);
        setGamesError(null);
        gamesLoadedRef.current = true;
        setStatusNote((prev) => (prev.includes('Kunde inte') ? `Hittade ${resolvedGames.length} spel.` : prev));
      } catch {
        setGamesError(t('selectGames.gameLoadError'));
        setStatusNote('Kunde inte hämta speltyper.');
        gamesLoadedRef.current = false;
      }
    };

    fetchSnapshot();
    fetchGames();

    const clearRoundAckTimeout = () => {
      if (roundAckTimeoutRef.current) {
        clearTimeout(roundAckTimeoutRef.current);
        roundAckTimeoutRef.current = null;
      }
    };

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const unsubscribeMessages = socket.onMessage((message) => {
      const type = (message as PlayersSnapshotEvent).type ?? (message as PlayersErrorEvent).type ?? (message as TourUpdateEvent).type;
      if (type === 'players_snapshot') {
        const payload = message as PlayersSnapshotEvent;
        const incomingTourId = payload.tourId ?? payload.TourId;
        setPlayers(payload.players ?? payload.Players ?? []);
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        setError(null);
        setStatusNote(`Websocket: ${payload.players?.length ?? payload.Players?.length ?? 0} spelare, tour ${incomingTourId ?? 'saknas'}`);
      } else if (type === 'tour.created') {
        const payload = message as TourCreatedEvent;
        const id = payload.tourId ?? payload.tourId;
        const name = payload.tourName ?? payload.TourName ?? null;
        if (id) {
          setTourId(id);
          if (name) setTourName(name);
          setStatusNote(`Tour skapad: ${name ?? id}`);
        }
      } else if (type === 'player_error') {
        const payload = message as PlayersErrorEvent;
        setError(payload.error ?? payload.Error ?? t('players.serviceError'));
        setStatusNote('Fel från players via websocket.');
      } else if ((message as TourUpdateEvent).tourId ?? (message as TourUpdateEvent).TourId) {
        const payload = message as TourUpdateEvent;
        const incomingTourId = payload.tourId ?? payload.TourId;
        const incomingTourName = payload.tourName ?? payload.TourName ?? null;
        if (incomingTourId) {
          setTourId(incomingTourId);
          if (incomingTourName) setTourName(incomingTourName);
          setError(null);
          setStatusNote(`Tour uppdaterad: ${incomingTourName ?? incomingTourId}`);
        }
      } else if (type === 'round.initiate.ack') {
        const payload = message as {
          roundId?: string;
          tourId?: string;
          requestId?: string;
        };
        const ackRequestId = payload.requestId ?? payload.requestId ?? null;
        if (pendingRoundRequestIdRef.current && ackRequestId && ackRequestId !== pendingRoundRequestIdRef.current) {
            return;
        }
        const ackRoundId = payload.roundId ?? payload.roundId;
        const ackTourId = payload.tourId ?? payload.tourId ?? tourId;
        pendingRoundRequestIdRef.current = null;
        clearRoundAckTimeout();
        setStarting(false);
        if (ackTourId) {
          setTourId(ackTourId);
        }
        if (ackRoundId) {
          const socketClient = getSharedBffWebSocket(apiBaseUrl);
          socketClient.send({ type: 'round.join', roundId: ackRoundId });
          socketClient.send({ type: 'round.requestState', roundId: ackRoundId });
          setCurrentRoundId(ackRoundId);
          setCurrentHole(1);
          const params: string[] = [];
          if (debug) params.push('debug=true');
          if (ackTourId) params.push(`tourId=${ackTourId}`);
          const query = params.length ? `?${params.join('&')}` : '';
          router.push(`/round/${ackRoundId}/hole/1/compact${query}`);
          logToBff('selectGames.round.initiate.ack', { roundId: ackRoundId, tourId: ackTourId, requestId: ackRequestId });
        } else {
          setError('BFF svarade utan roundId.');
          setStatusNote('Kunde inte starta rundan – inget roundId i svaret.');
          logToBff('selectGames.round.initiate.ack.missingRoundId', { tourId: ackTourId, requestId: ackRequestId }, 'warn');
        }
      }
    });
    const unsubscribeErrors = socket.onError((msg) => setError(msg));
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
        if (user?.token) {
          fetchSnapshot();
          fetchGames();
        }
      }
    });

    return () => {
      unsubscribeMessages();
      unsubscribeErrors();
      unsubscribeConnection();
      clearRoundAckTimeout();
    };
  }, [apiBaseUrl, user?.token, router, isNavigationReady, debug, gamesError, logToBff, tourId]);

  useEffect(() => {
    if (!roundIdIsValid || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchRound = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as RoundResponse;
        if (cancelled) return;
        const incomingCourse = data.courseName ?? data.CourseName ?? null;
        const incomingTourId = data.tourId ?? data.TourId ?? null;
        if (incomingCourse) setCourseName(incomingCourse);
        if (incomingTourId && !tourId) setTourId(incomingTourId);
        const incomingGameIds = data.gameDefinitionIds ?? data.GameDefinitionIds ?? [];
        if (incomingGameIds.length > 0 && !prefilledGamesRef.current) {
          setSelectedGameIds(new Set(incomingGameIds));
          prefilledGamesRef.current = true;
        }
      } catch {
        // Ignore; fallback to GUIDs.
      }
    };
    fetchRound();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, tourId, user?.token]);

  useEffect(() => {
    if (!tourId || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchTour = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as { tourName?: string; TourName?: string; name?: string; Name?: string };
        if (cancelled) return;
        const incomingTourName = data.tourName ?? data.TourName ?? data.name ?? data.Name ?? null;
        if (incomingTourName) setTourName(incomingTourName);
      } catch {
        // Ignore; fallback to GUIDs.
      }
    };
    fetchTour();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, tourId, user?.token]);

  const toggleGame = (id: string) => {
    setSelectedGameIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const startGame = () => {
    if (selectedGameIds.size === 0 || !tourId || !roundIdIsValid) {
      if (!roundId) {
        setError('Ingen runda att fortsätta. Gå tillbaka och starta om.');
      } else {
        setError(null);
      }
      return;
    }
    setStarting(true);
    setError(null);
    setStatusNote('Uppdaterar rundan via websocket...');
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    logToBff('selectGames.update.clicked', { tourId, gameCount: selectedGameIds.size, playerCount: players.length });
    const payload = {
      type: 'round.update',
      roundId,
      tourId,
      gameDefinitionIds: Array.from(selectedGameIds),
    };
    const sent = socket.send(payload);
    if (!sent) {
      setStatusNote('Väntar på websocket-anslutning...');
      logToBff('selectGames.round.update.queued', { tourId, gameCount: selectedGameIds.size });
    } else {
      logToBff('selectGames.round.update.sent', { tourId, gameCount: selectedGameIds.size });
    }
    const teamGamesSelected = Array.from(selectedGameIds).some((id) =>
      gameDefs.some((g) => g.id === id && (g.competitionType ?? '').toLowerCase() === 'team')
    );
    const mysterySelected = Array.from(selectedGameIds).some((id) =>
      gameDefs.some((g) => g.id === id && (g.name ?? '').toLowerCase() === 'mystery')
    );
    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${tourId}`);
    if (mysterySelected && teamGamesSelected) params.push('teamGame=1');
    const query = params.length ? `?${params.join('&')}` : '';
    if (mysterySelected) {
      router.push(`/round/${roundId}/mystery-prize${query}`);
    } else if (teamGamesSelected) {
      router.push(`/round/${roundId}/team-select${query}`);
    } else {
      setCurrentHole(1);
      router.push(`/round/${roundId}/hole/1/compact${query}`);
    }
    setStarting(false);
  };

  if (!isNavigationReady) {
    return null;
  }

  if (!user) {
    return <Redirect href="/login" />;
  }

  if (!navState?.key) {
    return null;
  }

  if (!roundId) {
    const debugParam = debug ? '?debug=true' : '';
    const backPath = tourId ? `/tour/${tourId}/round-init${debugParam}` : '/tournaments';
    return (
      <View style={styles.errorPage}>
        <Text style={styles.title}>Ingen runda hittades</Text>
        <Text style={styles.errorMeta}>Gå tillbaka och starta rundan igen.</Text>
        <Text style={styles.errorSupport}>Om felet kvarstår, kontakta support.</Text>
        <Pressable style={styles.errorButton} onPress={() => router.replace(backPath)}>
          <Text style={styles.errorButtonText}>Tillbaka</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <View style={styles.heroImageWrap}>
        <Image source={ChooseGameImage} style={styles.heroImage} resizeMode="cover" />
        <Text style={styles.title}>{t('selectGames.title')}</Text>
      </View>
      <UserBadge debug={debug} />
      <View style={styles.statusCard}>
        {debug ? <Text style={styles.statusText}>{statusNote}</Text> : null}
        <Text style={styles.statusText}>
          {t('selectGames.tourLabel')}: {stripGuidSuffix(tourName) ?? tourId ?? t('selectGames.missing')}
        </Text>
        <Text style={styles.statusText}>
          {t('selectGames.roundLabel')}: {stripGuidSuffix(courseName) ?? roundId ?? t('selectGames.missing')}
        </Text>
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t('selectGames.gameListTitle')}</Text>
        <View style={styles.gameGrid}>
          {gameDefs.map((game) => {
            const isSelected = selectedGameIds.has(game.id);
            const imageSource = getGameImageSource(game.name);
            const hasImage = !!imageSource;
            return (
              <Pressable
                key={game.id}
                onPress={() => toggleGame(game.id)}
                style={[styles.gameTile, isSelected && styles.gameTileSelected]}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isSelected }}
              >
                {hasImage ? (
                  <View style={styles.gameImageWrap}>
                    <Image source={imageSource} style={[styles.gameImage, isSelected && styles.gameImageSelected]} resizeMode="cover" />
                    <Pressable
                      style={styles.infoButton}
                      onPress={() => {
                        setInfoGame(game);
                        setInfoOpen(true);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={t('selectGames.gameInfo')}
                    >
                      <Text style={styles.infoButtonText}>{t('selectGames.infoIcon')}</Text>
                    </Pressable>
                  </View>
                ) : (
                  <View style={styles.gameImageEmpty} />
                )}
                <Text style={styles.gameTileName}>{getGameDisplayName(game, t('selectGames.gameNameFallback'))}</Text>
              </Pressable>
            );
          })}
        </View>
        {gamesError ? <Text style={styles.error}>{gamesError}</Text> : null}
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}
      <Pressable
        style={[styles.primaryButton, (selectedGameIds.size === 0 || !tourId || starting) && styles.primaryButtonDisabled]}
        onPress={startGame}
        disabled={selectedGameIds.size === 0 || !tourId || starting}
      >
        <Text style={styles.primaryButtonText}>{starting ? 'Startar...' : t('selectGames.startGame')}</Text>
      </Pressable>
      </ScrollView>
      <Modal
        visible={infoOpen}
        transparent
        animationType="fade"
        onRequestClose={closeInfo}
      >
        <View style={styles.infoOverlay}>
          <View style={styles.infoCard}>
            {infoVideoUri ? (
              <>
                <Text style={styles.infoTitle}>
                  {t('selectGames.infoVideoTitleByGame', {
                    name: getGameDisplayName(infoGame, t('selectGames.gameNameFallback')),
                  })}
                </Text>
                <Video
                  ref={infoVideoRef}
                  source={{ uri: infoVideoUri }}
                  style={[styles.infoVideo, { aspectRatio: infoVideoLayout.aspectRatio, maxWidth: infoVideoLayout.maxWidth }]}
                  resizeMode={ResizeMode.CONTAIN}
                  useNativeControls
                  shouldPlay={infoOpen}
                />
              </>
            ) : null}
            <Text style={styles.infoDescriptionTitle}>{t('selectGames.infoDescriptionTitle')}</Text>
            <Text style={styles.infoDescriptionText}>
              {infoGame?.description ?? infoGame?.info ?? t('selectGames.infoDescriptionEmpty')}
            </Text>
            <Pressable style={styles.infoCloseButton} onPress={closeInfo}>
              <Text style={styles.infoCloseButtonText}>{t('selectGames.closeVideo')}</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
}

function isGuid(value: string | null | undefined) {
  if (!value) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
}

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function stripGuidSuffix(value?: string | null) {
  if (!value) return value;
  return value.replace(/\s*[0-9a-fA-F]{32}$/u, '').replace(/\s*[0-9a-fA-F-]{36}$/u, '').trim();
}

function getGameImageSource(name?: string | null) {
  const slug = getGameSlug(name);
  if (!slug) return MysterySelectImage;
  return GAME_IMAGE_MAP[slug] ?? MysterySelectImage;
}

function getGameSlug(name?: string | null) {
  const fromName = (name ?? '').trim();
  if (!fromName) {
    return '';
  }
  return fromName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function resolveGameVideoUri(videoPath: string | undefined, apiBaseUrl: string, viewportWidth: number) {
  const raw = (videoPath ?? '').trim();
  if (!raw) {
    return null;
  }

  const preferMobile = viewportWidth > 0 && viewportWidth <= MOBILE_VIDEO_BREAKPOINT;
  const applyViewportVariant = (key: string) => {
    const normalizedKey = key.replace(/^\/+/, '');
    if (normalizedKey.startsWith('video/mobile/') || normalizedKey.startsWith('video/web/')) {
      return normalizedKey;
    }

    if (!normalizedKey.startsWith('video/')) {
      return normalizedKey;
    }

    const suffix = normalizedKey.slice('video/'.length);
    return preferMobile ? `video/mobile/${suffix}` : `video/web/${suffix}`;
  };

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1);
      if (key.startsWith('assets-generated/video/')) {
        const normalized = key.slice('assets-generated/'.length);
        return `${DELIVERY_ASSET_BASE_URL}/${applyViewportVariant(normalized)}`;
      }

      if (key.startsWith('video/')) {
        return `${DELIVERY_ASSET_BASE_URL}/${applyViewportVariant(key)}`;
      }
    }
    return null;
  }

  if (raw.startsWith('/video/')) {
    return `${DELIVERY_ASSET_BASE_URL}/${applyViewportVariant(raw)}`;
  }

  if (raw.startsWith('video/')) {
    return `${DELIVERY_ASSET_BASE_URL}/${applyViewportVariant(raw)}`;
  }

  if (raw.startsWith('/')) {
    return `${apiBaseUrl}${raw}`;
  }

  return `${apiBaseUrl}/${raw}`;
}

function resolveInfoVideoLayout(game: GameDefinition | null, preferMobile: boolean) {
  const webWidth = safePositive(game?.videoWebWidth, 1280);
  const webHeight = safePositive(game?.videoWebHeight, 720);
  const mobileWidth = safePositive(game?.videoMobileWidth, 720);
  const mobileHeight = safePositive(game?.videoMobileHeight, 1280);
  const width = preferMobile ? mobileWidth : webWidth;
  const height = preferMobile ? mobileHeight : webHeight;

  return {
    aspectRatio: width / height,
    maxWidth: width,
  };
}

function safePositive(value: number | undefined, fallback: number) {
  return typeof value === 'number' && value > 0 ? value : fallback;
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 9px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const ctaShadow = Platform.select({
  web: { boxShadow: '0px 3px 8px rgba(52,211,153,0.6)' },
  default: {
    shadowColor: '#34d399',
    shadowOpacity: 0.6,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
  },
});

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#0c1328',
  },
  heroImage: {
    width: '100%',
    height: 140,
    borderRadius: 18,
  },
  heroImageWrap: {
    position: 'relative',
    width: '100%',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 12,
    justifyContent: 'center',
    backgroundColor: '#0c1328',
  },
  hero: {
    fontSize: 52,
    textAlign: 'center',
    marginBottom: 4,
  },
  title: {
    position: 'absolute',
    top: 2,
    left: 16,
    right: 16,
    zIndex: 2,
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    color: '#f8fafc',
    textShadow: 'none',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusCard: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    gap: 4,
  },
  statusText: {
    fontSize: 13,
    color: '#d5e1ff',
  },
  errorPage: {
    flex: 1,
    backgroundColor: '#0c1328',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  errorMeta: {
    color: '#fca5a5',
    textAlign: 'center',
  },
  errorSupport: {
    color: '#9fb3ff',
    textAlign: 'center',
  },
  errorButton: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
    backgroundColor: '#1d4ed8',
  },
  errorButtonText: {
    color: '#f8fafc',
    fontWeight: '700',
  },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: '#0f1c3c',
    gap: 12,
    ...cardShadow,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#e5edff',
  },
  gameGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
  },
  gameTile: {
    width: '48%',
    backgroundColor: '#0a1430',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#223a70',
    padding: 8,
    minHeight: 88,
    ...cardShadow,
  },
  gameTileSelected: {
    opacity: 0.55,
  },
  gameImage: {
    width: '100%',
    height: 140,
    borderRadius: 10,
  },
  gameImageWrap: {
    position: 'relative',
  },
  gameImageEmpty: {
    width: '100%',
    height: 140,
    borderRadius: 10,
    backgroundColor: '#101a38',
  },
  infoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(12,19,40,0.78)',
    borderWidth: 1,
    borderColor: '#8bb3ff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoButtonText: {
    color: '#e5edff',
    fontSize: 14,
    fontWeight: '700',
  },
  gameImageSelected: {
    opacity: 0.6,
  },
  gameTileName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#e5edff',
    textAlign: 'center',
    marginTop: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    color: '#e5edff',
  },
  tourId: {
    fontSize: 18,
    fontWeight: '700',
    color: '#34d399',
    textAlign: 'center',
  },
  spinner: {
    marginTop: 4,
  },
  error: {
    color: '#ff7b7b',
  },
  infoOverlay: {
    flex: 1,
    backgroundColor: 'rgba(5,10,25,0.75)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  infoCard: {
    width: '100%',
    maxWidth: 900,
    backgroundColor: '#0f1c3c',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#24346b',
    gap: 12,
    ...cardShadow,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#e5edff',
    textAlign: 'center',
  },
  infoVideo: {
    width: '100%',
    height: undefined,
    borderRadius: 12,
    backgroundColor: '#0a1430',
  },
  infoDescriptionTitle: {
    marginTop: 4,
    color: '#cfe0ff',
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  infoDescriptionText: {
    color: '#e5edff',
    marginTop: -4,
    lineHeight: 18,
  },
  infoCloseButton: {
    alignSelf: 'center',
    backgroundColor: '#1d4ed8',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
  },
  infoCloseButtonText: {
    color: '#f8fafc',
    fontWeight: '700',
  },
  primaryButton: {
    marginTop: 8,
    backgroundColor: '#34d399',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#1aa374',
    ...ctaShadow,
  },
  primaryButtonDisabled: {
    backgroundColor: '#1f2d4f',
    borderColor: '#2c3a6a',
    shadowOpacity: 0,
  },
  primaryButtonText: {
    color: '#0c1328',
    fontWeight: '800',
    fontSize: 16,
  },
});
